import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MarketplaceHeader from '../../components/navigation/MarketplaceHeader';
import ServiceHero from './components/ServiceHero';
import ServiceDetails from './components/ServiceDetails';
import ReviewsSection from './components/ReviewsSection';
import RelatedServices from './components/RelatedServices';
import EnquiryModal from './components/EnquiryModal';
import Icon from '../../components/AppIcon';
import { trackEvent } from '../../hooks/useGoogleAnalytics';
import { trackServiceView, trackEnquiryOpen, trackEnquirySubmit, trackLeadConfirmed, trackLeadAsConversion,  } from '../../utils/analytics';
import { emailService } from '../../services/emailService';
import { leadService } from '../../services/leadService';

const DigitalServiceStandee = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Get subcategory data from location state
  const subcategoryId = location?.state?.subcategoryId;
  const subcategoryName = location?.state?.subcategoryName;
  const categoryId = location?.state?.categoryId;
  const categoryName = location?.state?.categoryName;
  const subcategoryData = location?.state?.subcategoryData; // Get complete subcategory data

  // Transform subcategory data into service format for components
  const serviceData = subcategoryData ? {
    id: subcategoryData?.id,
    name: subcategoryData?.name,
    category: categoryName,
    subCategory: subcategoryName,
    description: subcategoryData?.description,
    image: subcategoryData?.image,
    imageAlt: `${subcategoryData?.name} - Professional business service`,
    badges: ['Verified', 'Popular'],
    timeline: '3-7 Business Days',
    priceRange: 'Starting from ₹5,000',
    rating: 4.8,
    reviewCount: subcategoryData?.providers || 120,
    detailedDescription: subcategoryData?.description,
    keyFeatures: subcategoryData?.features || [],
    targetAudience: ['Small Businesses', 'Startups', 'Enterprises', 'Individuals'],
    deliverables: [
      {
        title: 'Complete Documentation',
        description: 'All necessary documents and certificates for your service'
      },
      {
        title: 'Expert Consultation',
        description: 'Professional guidance throughout the process'
      },
      {
        title: 'Compliance Support',
        description: 'Ensure all regulatory requirements are met'
      },
      {
        title: 'Post-Service Support',
        description: 'Ongoing assistance after service completion'
      }
    ],
    requiredDocuments: [
      {
        name: 'Identity Proof',
        description: 'Aadhaar Card, PAN Card, or Passport',
        required: true
      },
      {
        name: 'Address Proof',
        description: 'Utility bill, Bank statement, or Rental agreement',
        required: true
      },
      {
        name: 'Business Documents',
        description: 'Registration certificate, GST certificate (if applicable)',
        required: false
      },
      {
        name: 'Financial Documents',
        description: 'Bank statements, Financial records',
        required: false
      }
    ],
    processSteps: [
      {
        title: 'Initial Consultation',
        description: 'Discuss your requirements with our expert team',
        duration: '1 day'
      },
      {
        title: 'Document Collection',
        description: 'Submit all required documents for processing',
        duration: '1-2 days'
      },
      {
        title: 'Processing & Verification',
        description: 'Our team processes your request and verifies all information',
        duration: '2-3 days'
      },
      {
        title: 'Completion & Delivery',
        description: 'Receive your completed service with all documentation',
        duration: '1-2 days'
      }
    ]
  } : null;

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [subcategoryId]);

  useEffect(() => {
    if (subcategoryName) {
      trackEvent('service_standee_view', {
        page: 'digital-service-standee',
        subcategory_id: subcategoryId,
        subcategory_name: subcategoryName,
        category_id: categoryId
      });
      // Funnel Stage 3: Service detail viewed
      trackServiceView(subcategoryName, subcategoryId, categoryName, {
        source: 'digital_service_standee',
      });
    }
  }, [subcategoryId, subcategoryName, categoryId, categoryName]);

  const handleEnquiryClick = () => {
    trackEvent('enquiry_initiated', {
      subcategory_id: subcategoryId,
      subcategory_name: subcategoryName,
      page: 'digital-service-standee'
    });
    // Funnel Stage 4: Enquiry modal opened
    trackEnquiryOpen(subcategoryName, subcategoryId, { category_name: categoryName });
    setShowEnquiryModal(true);
  };

  const handleEnquirySubmit = async (formData) => {
    trackEvent('enquiry_submitted', {
      subcategory_id: subcategoryId,
      subcategory_name: subcategoryName,
      customer_name: formData?.name,
      page: 'digital-service-standee'
    });
    // Funnel Stage 5: Enquiry submitted
    trackEnquirySubmit(subcategoryName, subcategoryId, formData, { category_name: categoryName });

    // Send Resend notification and save lead to DB
    try {
      await emailService?.sendLeadNotification({
        name: formData?.name,
        email: formData?.email,
        phone: formData?.phone,
        company: formData?.company,
        serviceName: subcategoryName,
        urgency: formData?.urgency,
        budgetRange: formData?.budget,
        description: formData?.description,
        source: `Digital Service Standee - ${subcategoryName}`,
      });
    } catch (emailError) {
      console.error('Email notification failed:', emailError);
    }

    try {
      await leadService?.create({
        name: formData?.name,
        email: formData?.email,
        phone: formData?.phone,
        company: formData?.company,
        serviceRequired: subcategoryName,
        message: `Urgency: ${formData?.urgency}\nBudget: ${formData?.budget}\n\n${formData?.description}`,
        source: 'digital_standee_enquiry',
        status: 'new',
      });
      // Funnel Stage 6: Lead confirmed + conversion value
      trackLeadConfirmed(subcategoryName, 'digital_standee_enquiry', { category_name: categoryName });
      trackLeadAsConversion(subcategoryName, formData?.budget, 'digital_standee_enquiry');
    } catch (dbError) {
      console.error('Lead DB save failed:', dbError);
    }

    setShowEnquiryModal(false);
  };

  if (!subcategoryId || !subcategoryName) {
    return (
      <div className="min-h-screen bg-background">
        <MarketplaceHeader />
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 text-center">
          <div className="text-destructive text-lg mb-4">No subcategory selected</div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{subcategoryName ? `${subcategoryName} | ${categoryName} Services - ZippyAssist` : 'Service Details | ZippyAssist'}</title>
        <meta name="description" content={serviceData?.description
          ? `${serviceData?.description?.slice(0, 155)}...`
          : `Get professional ${subcategoryName} services from verified experts on ZippyAssist. Trusted by businesses in UK, US, Canada, UAE.`
        } />
        <meta name="keywords" content={`${subcategoryName}, ${categoryName}, business services, outsource ${subcategoryName}, ZippyAssist, verified service providers`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://zippyassist.in/digital-service-standee`} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${subcategoryName} | ${categoryName} Services - ZippyAssist`} />
        <meta property="og:description" content={serviceData?.description
          ? `${serviceData?.description?.slice(0, 155)}...`
          : `Professional ${subcategoryName} services from verified experts. Trusted by global businesses.`
        } />
        <meta property="og:url" content="https://zippyassist.in/digital-service-standee" />
        <meta property="og:site_name" content="ZippyAssist" />
        <meta property="og:image" content={serviceData?.image || 'https://zippyassist.in/assets/images/200x68-1775706855169.jpg'} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${subcategoryName} | ${categoryName} Services - ZippyAssist`} />
        <meta name="twitter:description" content={`Professional ${subcategoryName} services from verified experts on ZippyAssist.`} />
        <meta name="twitter:image" content={serviceData?.image || 'https://zippyassist.in/assets/images/200x68-1775706855169.jpg'} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": subcategoryName,
          "description": serviceData?.description || `Professional ${subcategoryName} services`,
          "serviceType": subcategoryName,
          "category": categoryName,
          "provider": {
            "@type": "Organization",
            "name": "ZippyAssist",
            "url": "https://zippyassist.in",
            "telephone": "+91-7039149001",
            "logo": {
              "@type": "ImageObject",
              "url": "https://zippyassist.in/assets/images/200x68-1775706855169.jpg"
            }
          },
          "areaServed": [
            { "@type": "Country", "name": "India" },
            { "@type": "Country", "name": "United Kingdom" },
            { "@type": "Country", "name": "United States" },
            { "@type": "Country", "name": "Canada" },
            { "@type": "Country", "name": "United Arab Emirates" }
          ],
          "offers": {
            "@type": "Offer",
            "priceCurrency": "INR",
            "price": "5000",
            "priceSpecification": {
              "@type": "PriceSpecification",
              "priceCurrency": "INR",
              "minPrice": "5000",
              "description": "Starting from ₹5,000"
            },
            "availability": "https://schema.org/InStock",
            "url": "https://zippyassist.in/digital-service-standee"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": serviceData?.reviewCount || 120,
            "bestRating": "5",
            "worstRating": "1"
          },
          "image": serviceData?.image || "https://zippyassist.in/assets/images/200x68-1775706855169.jpg"
        })}</script>

        {/* JSON-LD: BreadcrumbList */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://zippyassist.in" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://zippyassist.in/service-category-browse" },
            { "@type": "ListItem", "position": 3, "name": categoryName || "Category", "item": `https://zippyassist.in/service-category-browse?category=${encodeURIComponent(categoryName || '')}` },
            { "@type": "ListItem", "position": 4, "name": subcategoryName, "item": "https://zippyassist.in/digital-service-standee" }
          ]
        })}</script>
      </Helmet>
      <MarketplaceHeader />
      {isLoading ? (
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-muted rounded-xl"></div>
            <div className="h-48 bg-muted rounded-xl"></div>
            <div className="h-48 bg-muted rounded-xl"></div>
          </div>
        </div>
      ) : (
        <>
          <ServiceHero 
            subcategoryName={subcategoryName}
            categoryName={categoryName}
            service={serviceData || { name: subcategoryName, category: categoryName }}
            onEnquiryClick={handleEnquiryClick}
            onActionClick={handleEnquiryClick}
          />
          
          <ServiceDetails 
            subcategoryName={subcategoryName}
            subcategoryId={subcategoryId}
            service={serviceData || { id: subcategoryId, name: subcategoryName }}
          />

          {/* OTAssist Contact Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-muted">
            <div className="max-w-4xl mx-auto">
              <div className="bg-card rounded-xl border border-border p-6 md:p-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Icon name="Headphones" size={32} className="text-primary" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                    Get Expert Assistance
                  </h2>
                  <p className="text-base md:text-lg text-muted-foreground">
                    All customer enquiries are handled by OTAssist. Our team will connect you with the best service providers for your needs.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a
                    href="tel:+917039149001"
                    className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon name="Phone" size={24} className="text-primary" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-muted-foreground">Call Now</div>
                      <div className="font-semibold text-foreground">+91 7039149001</div>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/917039149001"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-full bg-green-600/10 flex items-center justify-center group-hover:bg-green-600/20 transition-colors">
                      <Icon name="MessageCircle" size={24} className="text-green-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-muted-foreground">WhatsApp</div>
                      <div className="font-semibold text-foreground">Chat Now</div>
                    </div>
                  </a>

                  <a
                    href="tel:+919310149001"
                    className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon name="Phone" size={24} className="text-primary" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-muted-foreground">Alternate Contact</div>
                      <div className="font-semibold text-foreground">+91 9310149001</div>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/919310149001"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon name="MessageCircle" size={24} className="text-primary" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-muted-foreground">Alternate WhatsApp</div>
                      <div className="font-semibold text-foreground">Chat with us</div>
                    </div>
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t border-border text-center">
                  <button
                    onClick={handleEnquiryClick}
                    className="w-full md:w-auto px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold"
                  >
                    Submit Enquiry Form
                  </button>
                </div>
              </div>
            </div>
          </section>

          <ReviewsSection 
            subcategoryName={subcategoryName}
            reviews={[]}
            overallRating={0}
            ratingDistribution={{}}
          />
          <RelatedServices 
            categoryId={categoryId} 
            currentSubcategoryId={subcategoryId}
            services={[]}
          />
        </>
      )}
      {showEnquiryModal && (
        <EnquiryModal
          isOpen={showEnquiryModal}
          subcategoryName={subcategoryName}
          serviceName={subcategoryName}
          onClose={() => setShowEnquiryModal(false)}
          onSubmit={handleEnquirySubmit}
        />
      )}
    </div>
  );
};

export default DigitalServiceStandee;